// CarryInPage 配下で共有する UI 非依存の純関数群。
// 注(Codex合意/A案): 搬入は出荷と異なり carryInRow が Lots を nested しない (Materials=搬入済QR)。
//   最頻材料Chip・ロット表の材料名/LotNo はデータ非保持のため省略 (代替表示)。
//   完了判定/搬入日は carried_in_at + require_material_count + Materials.length に統一。

import { ReactNode } from "react";
import { Typography } from "@mui/material";
import Time from "../../../util/Time";
import { mostFrequentMaterialLabel } from "../../../util/materialLabel";
import { carryInRow } from "../../../schema/console/carry_in";
import { MaterialMasterBase } from "../../../schema/generated/MaterialMaster";

export type LocationRow = { id: number; name: string; area_division_id: number };
export type LocationsById = Map<number, LocationRow>;
export type MaterialsById = Map<number, MaterialMasterBase>;

// 搬入済QRをロット単位に集約。各QRは api nest で Lot{material_id, lot_no} を持つ。
type LotGroup = { lotId: string; materialId: number | null; lotNo: string | null; carryOutId: string | null; count: number };
export function lotGroups(ci: carryInRow): LotGroup[] {
  const map = new Map<string, LotGroup>();
  ci.Materials.forEach((m) => {
    // lot_id(外部キー)は残るが、親ロットが論理削除されると Lot は null。実質ないものとして集約から除外する。
    if (!m.lot_id || !m.Lot) { return; }
    const g = map.get(m.lot_id) ?? { lotId: m.lot_id, materialId: m.Lot?.material_id ?? null, lotNo: m.Lot?.lot_no ?? null, carryOutId: m.Lot?.carry_out_id ?? null, count: 0 };
    g.count += 1;
    map.set(m.lot_id, g);
  });
  return Array.from(map.values());
}

// 最頻材料名 + 余剰種類数サフィックス(mock 同様の1チップ圧縮)。
export function mainMaterialLabel(ci: carryInRow, materialsById: MaterialsById): string {
  const materialIds = ci.Materials.map((m) => m.Lot?.material_id ?? null);
  return mostFrequentMaterialLabel(materialIds, (id) => materialsById.get(id)?.name ?? "?");
}

export const CarryInSelectingWorkCacheKey = "CarryInSelectingWorkData";

// 工事(Work)の一覧表示名。short_name を優先し、空なら official_name を使う。
export function workLabel(work: { short_name: string; official_name: string }): string {
  return work.short_name || work.official_name;
}

// 搬入完了 = 処理済み(carried_in_at確定) かつ 解決可能材料(Lot有り)が予定数を満たす。搬入完了日のみでの判定を避ける。
// 一覧チップ・搬入完了件数・搬入日グループ・並び替えの全てでこの判定に統一する。
export function isCarryInComplete(ci: carryInRow): boolean {
  return ci.carried_in_at != null && ci.require_material_count > 0 && resolvedMaterialCount(ci) >= ci.require_material_count;
}

export function carriedInEpoch(ci: carryInRow): number {
  return ci.carried_in_at ?? 0;
}

// 搬入済QRが属するロット種類数 (lot_id の distinct)。論理削除済みロット(Lot=null)は除外。
export function distinctLotCount(ci: carryInRow): number {
  return new Set(ci.Materials.filter((m) => m.Lot != null).map((m) => m.lot_id).filter((x): x is string => !!x)).size;
}

// 親ロットが論理削除された材料(Lot=null)は実質ないものとして、搬入数・完了判定から除外する。
export function resolvedMaterialCount(ci: carryInRow): number {
  return ci.Materials.filter((m) => m.Lot != null).length;
}

type CarryInStatusKind = "before" | "during" | "over" | "done";

// 搬入予定数(require_material_count)超過。サーバは超過を許容する(オフライン運用のため)ので、
// UI 側で出荷の「出荷個数超過 (n/m)」と同パターンの警告表示を出す。
export function isCarryInOver(ci: carryInRow): boolean {
  return ci.require_material_count > 0 && resolvedMaterialCount(ci) > ci.require_material_count;
}

// 未処理で材料0なら「搬入前」。処理済みなのに材料0(全ロット削除等)は搬入中扱いにする。
function carryInStatusKind(ci: carryInRow): CarryInStatusKind {
  const count = resolvedMaterialCount(ci);
  if (count === 0 && ci.carried_in_at == null) {
    return "before";
  }
  if (isCarryInOver(ci)) {
    return "over";
  }
  if (count < ci.require_material_count) {
    return "during";
  }
  return "done";
}

function carryInStatusText(ci: carryInRow): string {
  const count = resolvedMaterialCount(ci);
  const kind = carryInStatusKind(ci);
  if (kind === "before") {
    return "搬入前";
  }
  if (kind === "during") {
    return `搬入中 (${count}/${ci.require_material_count})`;
  }
  if (kind === "over") {
    return `搬入個数超過 (${count}/${ci.require_material_count})`;
  }
  if (ci.carried_in_at != null) {
    return `搬入済 (${new Time(ci.carried_in_at).Format("YYYY/MM/dd HH:mm:ss")})`;
  }
  return `搬入済 (${count}/${ci.require_material_count})`;
}

// 搬入状況の色付き表示。搬入前=赤 / 搬入中=標準色 / 搬入個数超過=赤 / 搬入済=緑
// (mock EventStatusBuilder 準拠。超過の赤は出荷の「出荷個数超過」と同パターン)。
export function carryInStatusNode(ci: carryInRow): ReactNode {
  const kind = carryInStatusKind(ci);
  let color = "text.primary";
  if (kind === "before" || kind === "over") {
    color = "error.main";
  }
  if (kind === "done") {
    color = "success.main";
  }
  return <Typography component="span" color={color}>{carryInStatusText(ci)}</Typography>;
}

// 搬入日グループのキー。完了(搬入済)は搬入日、未完了/未処理は (未搬入)。完了判定は一覧チップと統一。
export function carriedInDateKey(ci: carryInRow): string {
  if (isCarryInComplete(ci)) {
    return new Time(carriedInEpoch(ci)).Format("YYYY/MM/dd");
  }
  return "(未搬入)";
}
